import React, { useEffect, useState, useCallback, useRef } from "react";
import { matchesMember } from "../lib/memberSearch";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, ImagePlus, Users, UserPlus, LogOut, Trash2, X, Lock, Loader2, Camera, Pencil, Check, Pin, Megaphone, ChevronLeft, ChevronRight, MessageCircle, Move, Video, Flame, Film } from "lucide-react";
import { FiresideCard, FiresideForm, FiresideSuggest } from "../components/Fireside";
import api from "../lib/api";
import { useCrypto } from "../context/CryptoContext";
import { useAuth } from "../context/AuthContext";
import { SecureMedia, Avatar } from "../components/Media";
import { SignalPulse } from "../components/SignalPulse";
import { HearthNotifyMenu } from "../components/HearthNotifyMenu";
import { HearthPostEditor, HearthCommentEditor } from "../components/HearthEditors";
import { HearthTitleStyle } from "../components/HearthTitleStyle";
import { HearthEventForm, HearthEventButton, HearthEventCard } from "../components/HearthEvents";
import { PostCta } from "../components/HearthShareCards";
import { fontCss } from "../lib/beaconStyles";
import { encryptAndUpload, validateVideoFile, MAX_SLIDES } from "../lib/mediaUpload";
import { toast } from "sonner";

const DecText = ({ post }) => {
  const { decryptNetText } = useCrypto();
  const [txt, setTxt] = useState(post.enc ? null : post.text);
  useEffect(() => {
    let on = true;
    if (post.enc && post.ct) decryptNetText(post.ct, post.iv).then((t) => { if (on) setTxt(t || ""); });
    else setTxt(post.text || "");
    return () => { on = false; };
  }, [post.ct, post.iv, post.enc, post.text, decryptNetText]);
  if (txt === null) return <span className="text-zinc-600 text-sm">·····</span>;
  if (!txt) return null;
  return <span className="whitespace-pre-wrap">{txt}</span>;
};

export default function Hearth() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { hasNetwork, encryptNetText, encryptNetBytes } = useCrypto();
  const [meta, setMeta] = useState(null);
  const [posts, setPosts] = useState([]);
  const [tab, setTab] = useState("posts");
  const replays = posts.filter((p) => p.kind === "recap").sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [posting, setPosting] = useState(false);
  const [progress, setProgress] = useState("");
  const [showRoster, setShowRoster] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const swipeX = useRef(null);
  const [zoomed, setZoomed] = useState(false);
  const [savingBanner, setSavingBanner] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [posY, setPosY] = useState(50);
  const [zoom, setZoom] = useState(100);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [editingWelcome, setEditingWelcome] = useState(false);
  const [welcomeDraft, setWelcomeDraft] = useState("");
  const fileRef = useRef();

  const load = useCallback(async () => {
    try {
      const [m, f] = await Promise.all([api.get(`/hearths/${id}`), api.get(`/hearths/${id}/feed`)]);
      setMeta(m.data); setPosts(f.data.posts);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Can't open this community");
      nav("/hearths");
    } finally { setLoading(false); }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  // Validate picked media before queueing, same limits as the Signal feed
  // (15 min / 500 MB per video, up to 10 items per post).
  const addFiles = async (picked) => {
    const incoming = Array.from(picked || []);
    const room = MAX_SLIDES - files.length;
    if (room <= 0) { toast.error(`You can attach up to ${MAX_SLIDES} items per post.`); return; }
    const valid = [];
    for (const f of incoming.slice(0, room)) {
      const err = await validateVideoFile(f);
      if (err) { toast.error(err); continue; }
      valid.push(f);
    }
    if (incoming.length > room) toast.error(`Only ${MAX_SLIDES} items per post, some were not added.`);
    if (valid.length) setFiles((p) => [...p, ...valid]);
  };

  const submit = async () => {
    if (!text.trim() && !files.length) return;
    setPosting(true);
    try {
      const media = [];
      for (let i = 0; i < files.length; i++) {
        setProgress(`Encrypting & uploading ${i + 1}/${files.length}…`);
        media.push(await encryptAndUpload(files[i], { encryptNetBytes, hasNetwork, onPct: () => {} }));
      }
      setProgress("Posting…");
      let body = { media };
      if (text.trim()) {
        if (hasNetwork()) { const e = await encryptNetText(text.trim()); body = { ...body, ct: e.ct, iv: e.iv, enc: e.enc }; }
        else body.text = text.trim();
      }
      await api.post(`/hearths/${id}/posts`, body);
      setText(""); setFiles([]); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't post"); }
    finally { setPosting(false); setProgress(""); }
  };

  const [editingId, setEditingId] = useState(null);
  const [newEvent, setNewEvent] = useState(false);
  const [firesideEdit, setFiresideEdit] = useState(false);
  const del = async (pid) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      const wasRecap = posts.some((x) => x.id === pid && x.kind === "recap");
      await api.delete(`/hearths/${id}/posts/${pid}`);
      if (wasRecap) load(); else setPosts((p) => p.filter((x) => x.id !== pid));
    }
    catch { toast.error("Failed"); }
  };

  const togglePin = async (p) => {
    try { await api.post(`/hearths/${id}/posts/${p.id}/pin`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't pin"); }
  };

  const leave = async () => {
    if (!window.confirm("Leave this community?")) return;
    try { await api.post(`/hearths/${id}/leave`); toast("You left the community"); nav("/hearths"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const uploadBanner = async (file) => {
    if (!file) return;
    setSavingBanner(true);
    try {
      const m = await encryptAndUpload(file, { encryptNetBytes, hasNetwork, onPct: () => {} });
      await api.patch(`/hearths/${id}`, { banner: m.path, banner_iv: m.iv });
      setMeta((x) => ({ ...x, banner: m.path, banner_iv: m.iv }));
      toast.success("Banner updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't upload the banner"); }
    finally { setSavingBanner(false); }
  };

  const saveFraming = async () => {
    try {
      await api.patch(`/hearths/${id}`, { banner_pos_y: posY, banner_zoom: zoom });
      setMeta((x) => ({ ...x, banner_pos_y: posY, banner_zoom: zoom }));
      setAdjusting(false);
      toast.success("Banner framing saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save framing"); }
  };

  const saveTitle = async () => {
    const t = titleDraft.trim();
    if (!t || t === meta.title) { setEditingTitle(false); return; }
    try {
      await api.patch(`/hearths/${id}`, { title: t });
      setMeta((x) => ({ ...x, title: t }));
      setEditingTitle(false);
    } catch { toast.error("Couldn't rename"); }
  };

  const saveWelcome = async () => {
    try {
      await api.patch(`/hearths/${id}`, { welcome: welcomeDraft });
      setMeta((x) => ({ ...x, welcome: welcomeDraft.trim() || null }));
      setEditingWelcome(false);
      toast.success("Welcome note saved");
    } catch { toast.error("Couldn't save"); }
  };

  if (loading) return <div className="flex justify-center py-24"><SignalPulse size={44} /></div>;
  if (!meta) return null;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6" data-testid="hearth-page">
      <button onClick={() => nav("/hearths")} className="flex items-center gap-1.5 text-zinc-400 hover:text-white font-body text-sm mb-4"><ArrowLeft size={16} /> Communities</button>

      <p className="font-body text-[11px] tracking-[0.3em] text-[#14A89A] uppercase mb-3" data-testid="hearth-eyebrow">The Hearth</p>

      {/* Banner */}
      {(meta.banner || meta.is_owner) && (
        <div className="mb-4" data-testid="hearth-banner-wrap">
          <div className="relative w-full aspect-[3/2] max-h-[560px] overflow-hidden border border-white/10 bg-[#101010]" data-testid="hearth-banner">
            {meta.banner ? (
              <SecureMedia path={meta.banner} iv={meta.banner_iv} enc type="image" className="w-full h-full object-cover"
                imgStyle={{ objectPosition: `50% ${(adjusting ? posY : (meta.banner_pos_y ?? 50))}%`, transform: `scale(${(adjusting ? zoom : (meta.banner_zoom ?? 100)) / 100})`, transformOrigin: "center center", transition: adjusting ? "none" : "object-position 0.2s, transform 0.2s" }} />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-zinc-600 gap-1.5">
                <Camera size={22} />
                <span className="font-body text-xs">Add a banner for your community</span>
              </div>
            )}
            {meta.is_owner && !adjusting && (
              <div className="absolute bottom-3 right-3 flex items-center gap-2">
                {meta.banner && (
                  <button data-testid="hearth-banner-adjust" onClick={() => { setPosY(meta.banner_pos_y ?? 50); setZoom(meta.banner_zoom ?? 100); setAdjusting(true); }}
                    className="flex items-center gap-1.5 bg-black/70 backdrop-blur text-white px-3 py-1.5 font-body text-xs cursor-pointer border border-white/20 hover:bg-black/85"><Move size={13} /> Adjust</button>
                )}
                <label data-testid="hearth-banner-upload" className={`flex items-center gap-1.5 bg-black/70 backdrop-blur text-white px-3 py-1.5 font-body text-xs cursor-pointer border border-white/20 hover:bg-black/85 ${savingBanner ? "opacity-60 pointer-events-none" : ""}`}>
                  {savingBanner ? <><SignalPulse size={13} inline /> Uploading…</> : <><Camera size={13} /> {meta.banner ? "Change" : "Add banner"}</>}
                  <input type="file" accept="image/*" className="hidden" disabled={savingBanner} onChange={(e) => { uploadBanner(e.target.files?.[0]); e.target.value = ""; }} />
                </label>
              </div>
            )}
          </div>
          {/* Framing controls */}
          {meta.is_owner && meta.banner && adjusting && (
            <div className="border border-white/10 bg-[#121212] p-3 mt-2 space-y-3" data-testid="hearth-banner-framing">
              <div className="flex items-center gap-3">
                <span className="font-body text-[11px] uppercase tracking-wider text-zinc-500 w-16 shrink-0">Zoom</span>
                <input type="range" min="100" max="300" step="1" value={zoom} onChange={(e) => setZoom(Number(e.target.value))} data-testid="hearth-banner-zoom" className="flex-1 accent-[#14A89A]" />
                <span className="font-body text-xs text-zinc-400 w-10 text-right">{Math.round(zoom)}%</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-body text-[11px] uppercase tracking-wider text-zinc-500 w-16 shrink-0">Position</span>
                <input type="range" min="0" max="100" step="1" value={posY} onChange={(e) => setPosY(Number(e.target.value))} data-testid="hearth-banner-posy" className="flex-1 accent-[#14A89A]" />
                <span className="font-body text-xs text-zinc-400 w-10 text-right">{Math.round(posY)}%</span>
              </div>
              <div className="flex items-center justify-end gap-2 pt-1">
                <button onClick={() => setAdjusting(false)} data-testid="hearth-banner-framing-cancel" className="font-body text-xs text-zinc-500 hover:text-white px-3 py-1.5">Cancel</button>
                <button onClick={saveFraming} data-testid="hearth-banner-framing-save" className="bg-[#14A89A] text-[#0A0A0A] font-semibold px-4 py-1.5 font-body text-xs">Save framing</button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
        <div className="min-w-0">
          {editingTitle ? (
            <div className="flex items-center gap-2">
              <input autoFocus value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)} maxLength={80}
                onKeyDown={(e) => { if (e.key === "Enter") saveTitle(); if (e.key === "Escape") setEditingTitle(false); }}
                data-testid="hearth-title-input"
                className="bg-transparent border-b border-[#14A89A]/60 font-display text-2xl text-white outline-none min-w-0" />
              <button onClick={saveTitle} data-testid="hearth-title-save" className="text-[#14A89A]"><Check size={18} /></button>
              <button onClick={() => setEditingTitle(false)} className="text-zinc-500"><X size={18} /></button>
            </div>
          ) : (
            <h1 className="font-display text-2xl text-white flex items-center gap-2" style={{ fontFamily: meta.title_font ? fontCss(meta.title_font) : undefined, color: meta.title_color || undefined }} data-testid="hearth-title">
              <Lock size={16} className="text-[#14A89A] shrink-0" /> <span className="break-words">{meta.title}</span>
              {meta.is_owner && <button onClick={() => { setTitleDraft(meta.title || ""); setEditingTitle(true); }} data-testid="hearth-title-edit" className="text-zinc-600 hover:text-[#14A89A]"><Pencil size={14} /></button>}
              {meta.is_owner && <HearthTitleStyle hearthId={id} meta={meta} onSaved={(patch) => setMeta((m) => ({ ...m, ...patch }))} />}
            </h1>
          )}
          <p className="font-body text-xs text-zinc-500 mt-1">{meta.members.length} member{meta.members.length === 1 ? "" : "s"} · private &amp; encrypted</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => nav(`/meet?from=${id}`)} data-testid="hearth-meetup-btn" title="Encrypted Meet Up" aria-label="Encrypted Meet Up"
            className="flex items-center justify-center border border-white/15 text-zinc-300 hover:text-[#14A89A] hover:border-[#14A89A]/50 w-9 h-9 transition-colors"><Video size={15} /></button>
          <button onClick={() => setShowRoster(true)} data-testid="hearth-roster-btn" className="flex items-center gap-1.5 border border-white/15 text-zinc-300 px-3 py-2 font-body text-xs hover:bg-white/5"><Users size={14} /> Members</button>
          {meta.my_notify && <HearthNotifyMenu hearthId={id} level={meta.my_notify} onChange={(l) => setMeta((x) => ({ ...x, my_notify: l }))} />}
          {meta.is_owner && <button onClick={() => setShowInvite(true)} data-testid="hearth-invite-btn" className="flex items-center gap-1.5 bg-[#14A89A] text-[#0A0A0A] font-semibold px-3 py-2 font-body text-xs"><UserPlus size={14} /> Invite</button>}
          {!meta.is_owner && meta.my_role !== "council" && <button onClick={leave} data-testid="hearth-leave-btn" className="flex items-center gap-1.5 border border-white/15 text-zinc-400 px-3 py-2 font-body text-xs hover:bg-white/5"><LogOut size={14} /> Leave</button>}
        </div>
      </div>
      {meta.my_role === "council" && <p className="font-body text-[11px] text-amber-400/80 mb-3">Council safety view: you are not a member.</p>}

      {/* Welcome note */}
      {editingWelcome ? (
        <div className="border border-[#14A89A]/40 bg-[#121212] p-3 my-4" data-testid="hearth-welcome-editor">
          <textarea autoFocus value={welcomeDraft} onChange={(e) => setWelcomeDraft(e.target.value)} maxLength={500} rows={3}
            placeholder="Write a warm welcome for members…"
            className="w-full bg-transparent text-white font-body text-sm resize-none outline-none placeholder:text-zinc-600" />
          <div className="flex items-center justify-end gap-2 mt-2">
            <button onClick={() => setEditingWelcome(false)} className="text-zinc-400 font-body text-xs px-3 py-1.5">Cancel</button>
            <button onClick={saveWelcome} data-testid="hearth-welcome-save" className="bg-[#14A89A] text-[#0A0A0A] font-semibold px-4 py-1.5 font-body text-xs">Save</button>
          </div>
        </div>
      ) : meta.welcome ? (
        <div className="border-l-2 border-[#14A89A]/50 bg-white/[0.02] pl-3 py-2 my-4 group relative" data-testid="hearth-welcome">
          <p className="font-body text-sm text-zinc-300 whitespace-pre-wrap">{meta.welcome}</p>
          {meta.is_owner && <button onClick={() => { setWelcomeDraft(meta.welcome || ""); setEditingWelcome(true); }} data-testid="hearth-welcome-edit" className="absolute top-2 right-2 text-zinc-600 hover:text-[#14A89A] opacity-0 group-hover:opacity-100 transition-opacity"><Pencil size={13} /></button>}
        </div>
      ) : meta.is_owner ? (
        <button onClick={() => { setWelcomeDraft(""); setEditingWelcome(true); }} data-testid="hearth-welcome-add" className="flex items-center gap-1.5 text-zinc-500 hover:text-[#14A89A] font-body text-xs my-4"><Pencil size={13} /> Add a welcome note</button>
      ) : null}

      <HearthAnnouncement meta={meta} hearthId={id} isOwner={meta.is_owner} onChange={setMeta} />
      {firesideEdit
        ? <FiresideForm hearthId={id} onDone={(fs) => { setFiresideEdit(false); if (fs) setMeta((m) => ({ ...m, fireside: fs })); }} />
        : meta.fireside
          ? <FiresideCard hearthId={id} fs={meta.fireside} isOwner={meta.is_owner} onChange={(fs) => setMeta((m) => ({ ...m, fireside: fs }))} />
          : meta.is_owner && <FiresideSuggest onStart={() => setFiresideEdit(true)} />}
      {meta.is_owner && (newEvent
        ? <HearthEventForm hearthId={id} onDone={(ok) => { setNewEvent(false); if (ok) load(); }} />
        : <div className="flex items-center gap-5 flex-wrap">
            <HearthEventButton onClick={() => setNewEvent(true)} />
            {!meta.fireside && !firesideEdit && <button onClick={() => setFiresideEdit(true)} data-testid="fireside-add" className="flex items-center gap-1.5 text-zinc-500 hover:text-[#E8A33D] font-body text-xs my-2"><Flame size={13} /> Set up a Fireside</button>}
          </div>)}

      {/* Composer */}
      {meta.my_role !== "council" && (
        <div className="border border-white/10 bg-[#121212] p-3 my-4" data-testid="hearth-composer">
          <textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="hearth-post-input" placeholder="Share with your community…" rows={2}
            className="w-full bg-transparent text-white font-body text-sm resize-none outline-none placeholder:text-zinc-600" />
          {files.length > 0 && <div className="flex gap-2 flex-wrap my-2">{files.map((f, i) => (
            <div key={i} className="text-[11px] text-zinc-400 bg-white/5 px-2 py-1 flex items-center gap-1">{f.name.slice(0, 18)}<button onClick={() => setFiles((p) => p.filter((_, x) => x !== i))}><X size={11} /></button></div>))}</div>}
          <div className="flex items-center justify-between mt-2">
            <label data-testid="hearth-attach-btn" className="text-zinc-400 hover:text-[#14A89A] flex items-center gap-1.5 font-body text-xs cursor-pointer"><ImagePlus size={16} /> Photo / video
              <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
            </label>
            <button onClick={submit} disabled={posting || (!text.trim() && !files.length)} data-testid="hearth-post-submit" className="bg-[#14A89A] text-[#0A0A0A] font-semibold px-4 py-2 font-body text-xs disabled:opacity-40 flex items-center gap-1.5">
              {posting ? <><SignalPulse size={14} inline /> {progress || "Posting…"}</> : <><Send size={14} /> Post</>}
            </button>
          </div>
        </div>
      )}

      {/* Feed */}
      <div className="flex items-center gap-1 border-b border-white/10 mb-4" role="tablist" data-testid="hearth-tabs">
        {[["posts", "Posts", MessageCircle], ["replays", `Replays${replays.length ? ` (${replays.length})` : ""}`, Film]].map(([k, l, I]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`hearth-tab-${k}`}
            className={`flex items-center gap-1.5 px-3 py-2 font-body text-xs -mb-px border-b-2 transition-colors ${tab === k ? "border-[#14A89A] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}><I size={13} /> {l}</button>
        ))}
      </div>
      <div className="space-y-4">
        {tab === "posts" && posts.length === 0 && <p className="text-center text-zinc-600 font-body text-sm py-10">No posts yet, start the conversation.</p>}
        {tab === "replays" && replays.length === 0 && <p className="text-center text-zinc-600 font-body text-sm py-10" data-testid="hearth-replays-empty">No replays yet. When a session recap is posted, it lands here so anyone can catch up.</p>}
        {(tab === "replays" ? replays : posts).map((p) => (
          <div key={p.id} data-testid={`hearth-post-${p.id}`} className={`border bg-[#121212] p-4 ${p.pinned ? "border-[#14A89A]/40" : "border-white/10"}`}>
            {p.pinned && <div className="flex items-center gap-1 text-[#14A89A] font-body text-[10px] uppercase tracking-wider mb-2" data-testid={`hearth-pinned-badge-${p.id}`}><Pin size={11} className="fill-[#14A89A]" /> Pinned</div>}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Avatar name={p.author?.name} src={p.author?.avatar} iv={p.author?.avatar_iv} size={32} />
                <div>
                  <div className="font-body text-sm text-white">{p.author?.name || "Member"}</div>
                  <div className="font-body text-[11px] text-zinc-600">{new Date(p.created_at).toLocaleString()}{p.edited_at && <span data-testid={`hearth-edited-${p.id}`} title={`Edited ${new Date(p.edited_at).toLocaleString()}`}> · edited</span>}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {meta.is_owner && <button onClick={() => togglePin(p)} data-testid={`hearth-pin-${p.id}`} title={p.pinned ? "Unpin" : "Pin"} className={p.pinned ? "text-[#14A89A]" : "text-zinc-600 hover:text-[#14A89A]"}><Pin size={14} className={p.pinned ? "fill-[#14A89A]" : ""} /></button>}
                {p.author_id === user.id && !p.event && editingId !== p.id && <button onClick={() => setEditingId(p.id)} data-testid={`hearth-edit-${p.id}`} title="Edit post" className="text-zinc-600 hover:text-[#14A89A]"><Pencil size={14} /></button>}
                {(p.author_id === user.id || meta.is_owner) && <button onClick={() => del(p.id)} data-testid={`hearth-del-${p.id}`} className="text-zinc-600 hover:text-red-400"><Trash2 size={14} /></button>}
              </div>
            </div>
            {editingId === p.id ? (
              <div className="mb-2"><HearthPostEditor hearthId={id} post={p} onDone={(saved) => { setEditingId(null); if (saved) load(); }} /></div>
            ) : <>
              {p.event && <HearthEventCard hearthId={id} post={p} isOwner={meta.is_owner} onSaved={load} />}
              {(p.kind === "lesson_share" || p.kind === "recap") && <PostCta post={p} part="head" />}
              <div className="font-body text-sm text-zinc-200 mb-2"><DecText post={p} /></div>
            </>}
            {editingId !== p.id && p.media && p.media.length > 0 && (
              <div className={`grid gap-2 ${p.media.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                {p.media.map((m, i) => (
                  <SecureMedia key={i} path={m.path} iv={m.iv} enc={p.enc} type={m.type} thumb={m.thumb} thumbIv={m.thumb_iv} lazy onExpand={(u) => setLightbox({ media: p.media, index: i, enc: p.enc, pre: { [i]: u } })} className="w-full max-h-[420px] object-cover rounded" />
                ))}
              </div>
            )}
            {editingId !== p.id && (p.kind === "lesson_share" || p.kind === "recap") && <PostCta post={p} part="button" />}
            <HearthReactions hearthId={id} post={p} userId={user.id} onReacted={load} />
            <HearthComments hearthId={id} post={p} onAdded={load} disabled={meta.my_role === "council"} userId={user.id} />
          </div>
        ))}
      </div>

      {showRoster && <RosterModal meta={meta} hearthId={id} onClose={() => setShowRoster(false)} onChanged={load} />}
      {showInvite && <InviteModal hearthId={id} onClose={() => setShowInvite(false)} />}
      {lightbox && (() => {
        const items = lightbox.media || [];
        const idx = lightbox.index;
        const cur = items[idx];
        const go = (d) => { setZoomed(false); setLightbox((lb) => ({ ...lb, index: (lb.index + d + items.length) % items.length })); };
        const near = items.length > 1 ? [...new Set([(idx + 1) % items.length, (idx - 1 + items.length) % items.length])].filter((n) => n !== idx && items[n].type !== "video") : [];
        return (
          <div
            className="fixed inset-0 z-[90] bg-black/90 flex items-center justify-center p-4"
            onClick={() => { setZoomed(false); setLightbox(null); }}
            data-testid="hearth-lightbox"
            style={{ touchAction: "none" }}
            onTouchStart={(e) => { swipeX.current = e.touches.length === 1 && !zoomed ? e.touches[0].clientX : null; }}
            onTouchMove={(e) => { if (e.touches.length > 1) swipeX.current = null; }}
            onTouchEnd={(e) => { if (swipeX.current == null || zoomed || e.touches.length) return; const dx = e.changedTouches[0].clientX - swipeX.current; swipeX.current = null; if (Math.abs(dx) > 50 && items.length > 1) go(dx < 0 ? 1 : -1); }}
          >
            <button onClick={() => { setZoomed(false); setLightbox(null); }} data-testid="hearth-lightbox-close" className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white p-2 rounded-full transition-colors z-10"><X size={20} /></button>
            {items.length > 1 && (
              <button onClick={(e) => { e.stopPropagation(); go(-1); }} data-testid="hearth-lightbox-prev" className="absolute left-3 md:left-6 bg-white/10 hover:bg-white/20 text-white p-2 rounded-full transition-colors z-10"><ChevronLeft size={24} /></button>
            )}
            <div onClick={(e) => e.stopPropagation()} className="max-w-full max-h-[88vh] flex items-center justify-center">
              <SecureMedia key={idx} path={cur.path} iv={cur.iv} enc={lightbox.enc} type={cur.type} preUrl={(lightbox.pre || {})[idx]} fullscreen onZoomChange={setZoomed} />
              <div className="hidden" aria-hidden="true">{near.map((n) => <SecureMedia key={`pre-${n}`} path={items[n].path} iv={items[n].iv} enc={lightbox.enc} type={items[n].type} fullscreen />)}</div>
            </div>
            {items.length > 1 && (
              <button onClick={(e) => { e.stopPropagation(); go(1); }} data-testid="hearth-lightbox-next" className="absolute right-3 md:right-6 bg-white/10 hover:bg-white/20 text-white p-2 rounded-full transition-colors z-10"><ChevronRight size={24} /></button>
            )}
            {items.length > 1 && (
              <div data-testid="hearth-lightbox-count" className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/60 text-white font-body text-xs px-3 py-1.5 rounded-full">{idx + 1} / {items.length}</div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

const HearthAnnouncement = ({ meta, hearthId, isOwner, onChange }) => {
  const { hasNetwork, encryptNetText, decryptNetText } = useCrypto();
  const ann = meta.announcement;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [annPlain, setAnnPlain] = useState("");
  const key = `hearth_ann_${hearthId}`;

  useEffect(() => {
    if (!ann) { setDismissed(false); setAnnPlain(""); return; }
    const d = parseInt(localStorage.getItem(key) || "0", 10);
    setDismissed(d >= (ann.rev || 0));
    let on = true;
    if (ann.enc && ann.ct) decryptNetText(ann.ct, ann.iv).then((t) => { if (on) setAnnPlain(t || ""); });
    else setAnnPlain(ann.text || "");
    return () => { on = false; };
  }, [ann, key, decryptNetText]);

  const dismiss = () => { localStorage.setItem(key, String(ann?.rev || 0)); setDismissed(true); };

  const save = async () => {
    const t = draft.trim();
    if (!t) return;
    setBusy(true);
    try {
      let body = {};
      if (hasNetwork()) { const e = await encryptNetText(t); body = { ct: e.ct, iv: e.iv, enc: e.enc }; }
      else body = { text: t };
      const { data } = await api.put(`/hearths/${hearthId}/announcement`, body);
      onChange({ ...meta, announcement: data.announcement });
      setEditing(false); setDraft("");
      toast.success("Announcement posted");
    } catch { toast.error("Couldn't save the announcement"); } finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm("Clear the announcement?")) return;
    try { await api.delete(`/hearths/${hearthId}/announcement`); onChange({ ...meta, announcement: null }); }
    catch { toast.error("Failed"); }
  };

  if (editing) {
    return (
      <div className="border border-[#14A89A]/40 bg-[#14A89A]/[0.06] p-3 my-4" data-testid="hearth-announce-editor">
        <div className="flex items-center gap-1.5 text-[#14A89A] font-body text-[11px] uppercase tracking-wider mb-2"><Megaphone size={13} /> Announcement</div>
        <textarea autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={280} rows={2}
          placeholder="A short, important note for the whole community…"
          className="w-full bg-transparent text-white font-body text-sm resize-none outline-none placeholder:text-zinc-600" />
        <div className="flex items-center justify-end gap-2 mt-2">
          <button onClick={() => { setEditing(false); setDraft(""); }} className="text-zinc-400 font-body text-xs px-3 py-1.5">Cancel</button>
          <button onClick={save} disabled={busy || !draft.trim()} data-testid="hearth-announce-save" className="bg-[#14A89A] text-[#0A0A0A] font-semibold px-4 py-1.5 font-body text-xs disabled:opacity-40">{busy ? "Posting…" : "Post announcement"}</button>
        </div>
      </div>
    );
  }

  if (isOwner && !ann) {
    return (
      <button onClick={() => { setDraft(""); setEditing(true); }} data-testid="hearth-announce-add" className="flex items-center gap-1.5 text-zinc-500 hover:text-[#14A89A] font-body text-xs my-4"><Megaphone size={13} /> Post an announcement</button>
    );
  }

  if (ann && (isOwner || !dismissed)) {
    return (
      <div className="border border-[#14A89A]/40 bg-[#14A89A]/[0.08] p-3 my-4 flex items-start gap-2.5" data-testid="hearth-announcement">
        <Megaphone size={16} className="text-[#14A89A] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-[#14A89A] font-body text-[10px] uppercase tracking-wider mb-0.5">Announcement</div>
          <p className="font-body text-sm text-zinc-100 whitespace-pre-wrap break-words"><DecText post={ann} /></p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {isOwner ? (
            <>
              <button onClick={() => { setDraft(annPlain || ""); setEditing(true); }} data-testid="hearth-announce-edit" title="Edit" className="text-zinc-500 hover:text-[#14A89A]"><Pencil size={13} /></button>
              <button onClick={clear} data-testid="hearth-announce-clear" title="Clear" className="text-zinc-500 hover:text-red-400"><Trash2 size={13} /></button>
            </>
          ) : (
            <button onClick={dismiss} data-testid="hearth-announce-dismiss" title="Dismiss" className="text-zinc-500 hover:text-white"><X size={14} /></button>
          )}
        </div>
      </div>
    );
  }
  return null;
};

const HEARTH_REACTS = ["❤️", "🔥", "🙏", "😂", "💪", "🎉"];

const HearthReactions = ({ hearthId, post, userId, onReacted }) => {
  const [busy, setBusy] = useState(false);
  const reactions = post.reactions || {};
  const react = async (emoji) => {
    if (busy) return;
    setBusy(true);
    try { await api.post(`/hearths/${hearthId}/posts/${post.id}/react`, { emoji }); onReacted(); }
    catch { toast.error("Couldn't react"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-3" data-testid={`hearth-reactions-${post.id}`}>
      {HEARTH_REACTS.map((e) => {
        const users = reactions[e] || [];
        const mine = users.includes(userId);
        return (
          <button
            key={e}
            onClick={() => react(e)}
            data-testid={`hearth-react-${post.id}-${e}`}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-full border font-body text-xs transition-colors ${mine ? "border-[#14A89A] bg-[#14A89A]/10" : "border-white/10 hover:border-white/30"}`}
          >
            <span className="leading-none">{e}</span>
            {users.length > 0 && <span className={mine ? "text-[#14A89A]" : "text-zinc-400"}>{users.length}</span>}
          </button>
        );
      })}
    </div>
  );
};

const HearthComments = ({ hearthId, post, onAdded, disabled, userId }) => {
  const { hasNetwork, encryptNetText } = useCrypto();
  const [editing, setEditing] = useState(null);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const comments = post.comments || [];
  const add = async () => {
    if (!val.trim()) return;
    setBusy(true);
    try {
      let body = {};
      if (hasNetwork()) { const e = await encryptNetText(val.trim()); body = { ct: e.ct, iv: e.iv, enc: e.enc }; }
      else body = { text: val.trim() };
      await api.post(`/hearths/${hearthId}/posts/${post.id}/comment`, body);
      setVal(""); onAdded();
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 border-t border-white/5 pt-3">
      <div className="flex items-center gap-1.5 text-zinc-500 font-body text-[11px] mb-2" data-testid={`hearth-comment-count-${post.id}`}>
        <MessageCircle size={13} /> {comments.length > 0 ? `${comments.length} ${comments.length === 1 ? "comment" : "comments"}` : "Be the first to comment"}
      </div>
      {comments.map((c) => (
        <div key={c.id} className="flex items-start gap-2 mb-2">
          <Avatar name={c.author?.name} src={c.author?.avatar} iv={c.author?.avatar_iv} size={24} />
          <div className="min-w-0 max-w-full">
            <div className="bg-white/5 rounded-2xl rounded-tl-sm px-3 py-1.5 max-w-full">
              <span className="block text-white font-body text-xs font-medium">{c.author?.name || "Member"}</span>
              {editing === c.id
                ? <HearthCommentEditor hearthId={hearthId} postId={post.id} comment={c} onDone={(saved) => { setEditing(null); if (saved) onAdded(); }} />
                : <span className="text-zinc-300 font-body text-xs break-words"><DecText post={c} /></span>}
            </div>
            {editing !== c.id && (c.edited_at || c.author_id === userId) && (
              <div className="flex gap-2 pl-3 mt-0.5 font-body text-[10px] text-zinc-600">
                {c.edited_at && <span data-testid={`hearth-comment-edited-${c.id}`}>edited</span>}
                {c.author_id === userId && !disabled && <button onClick={() => setEditing(c.id)} data-testid={`hearth-comment-edit-${c.id}`} className="hover:text-[#14A89A]">Edit</button>}
              </div>
            )}
          </div>
        </div>
      ))}
      {!disabled && (
        <div className="flex items-center gap-2 mt-2 bg-white/5 rounded-full pl-4 pr-1.5 py-1 focus-within:ring-1 focus-within:ring-[#14A89A]/50">
          <input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} data-testid={`hearth-comment-input-${post.id}`} placeholder="Write a comment…" className="flex-1 bg-transparent text-white font-body text-xs py-1.5 outline-none placeholder:text-zinc-500" />
          <button onClick={add} disabled={busy || !val.trim()} data-testid={`hearth-comment-send-${post.id}`} className="flex items-center gap-1 bg-[#14A89A] text-[#0A0A0A] font-body text-xs font-medium px-3 py-1.5 rounded-full disabled:opacity-30 shrink-0"><Send size={12} /> Send</button>
        </div>
      )}
    </div>
  );
};

const RosterModal = ({ meta, hearthId, onClose, onChanged }) => {
  const remove = async (uid) => {
    if (!window.confirm("Remove this member?")) return;
    try { await api.post(`/hearths/${hearthId}/members/${uid}/remove`); onChanged(); toast("Member removed"); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="hearth-roster-modal">
      <div className="bg-[#0F0F0F] border border-white/10 w-full max-w-md max-h-[80vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><h3 className="font-display text-xl text-white">Members</h3><button onClick={onClose}><X size={18} className="text-zinc-500" /></button></div>
        {meta.members.map((m) => (
          <div key={m.id} className="flex items-center justify-between py-2">
            <div className="flex items-center gap-2"><Avatar name={m.name} src={m.avatar} iv={m.avatar_iv} size={30} /><span className="font-body text-sm text-zinc-200">{m.name || "Member"}{m.role === "owner" && <span className="text-[10px] text-[#14A89A] ml-1.5 uppercase tracking-wide">Owner</span>}</span></div>
            {meta.is_owner && m.role !== "owner" && <button onClick={() => remove(m.id)} className="text-zinc-600 hover:text-red-400"><Trash2 size={14} /></button>}
          </div>
        ))}
      </div>
    </div>
  );
};

const InviteModal = ({ hearthId, onClose }) => {
  const [cands, setCands] = useState(null);
  const [q, setQ] = useState("");
  const [invited, setInvited] = useState({});
  useEffect(() => { api.get(`/hearths/${hearthId}/invite-candidates`).then(({ data }) => setCands(data.candidates)).catch(() => setCands([])); }, [hearthId]);
  const invite = async (uid) => {
    try { await api.post(`/hearths/${hearthId}/invite`, { user_id: uid }); setInvited((p) => ({ ...p, [uid]: true })); toast.success("Invite sent"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const filtered = (cands || []).filter((c) => matchesMember(c, q));
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="hearth-invite-modal">
      <div className="bg-[#0F0F0F] border border-white/10 w-full max-w-md max-h-[80vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3"><h3 className="font-display text-xl text-white">Invite members</h3><button onClick={onClose}><X size={18} className="text-zinc-500" /></button></div>
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="hearth-invite-search" placeholder="Search members…" className="w-full bg-[#1a1a1a] border border-white/10 text-white font-body text-sm px-3 py-2 mb-3 outline-none" />
        {cands === null ? <div className="flex justify-center py-6"><SignalPulse size={30} /></div> : filtered.length === 0 ? <p className="text-zinc-600 font-body text-sm text-center py-6">No members found.</p> : filtered.slice(0, 60).map((c) => (
          <div key={c.id} data-testid={`invite-cand-${c.id}`} className="flex items-center justify-between py-2">
            <div className="flex items-center gap-2"><Avatar name={c.name} src={c.avatar} iv={c.avatar_iv} size={30} /><span className="font-body text-sm text-zinc-200">{c.name || "Member"}</span></div>
            <button onClick={() => invite(c.id)} disabled={invited[c.id]} className="text-xs font-body px-3 py-1.5 border border-[#14A89A]/50 text-[#14A89A] disabled:opacity-40">{invited[c.id] ? "Invited" : "Invite"}</button>
          </div>
        ))}
      </div>
    </div>
  );
};
